/**
 * ReprintConfirmDialog Component
 *
 * Confirmation dialog shown when user attempts to reprint labels
 * for a batch that has already been printed.
 *
 * Uses Dialog (not AlertDialog) per frontend design review -
 * AlertDialog is for destructive confirmations, this is a warning.
 */

import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface ReprintConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Batch number for display */
  batchNumber: string;
  /** When labels were last printed */
  lastPrintedAt: string;
  /** Callback when user confirms reprint */
  onConfirm: () => void;
  /** Whether the confirm action is in progress */
  isConfirming?: boolean;
}

export function ReprintConfirmDialog({
  open,
  onOpenChange,
  batchNumber,
  lastPrintedAt,
  onConfirm,
  isConfirming = false,
}: ReprintConfirmDialogProps) {
  const { t } = useTranslation('production');
  const handleConfirm = () => {
    onConfirm();
    // Dialog will be closed by parent after print completes
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            {t('batch.reprint.reprintLabelsQuestion')}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {t('batch.reprint.alreadyPrinted', { batchNumber, lastPrintedAt })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 text-sm text-muted-foreground">
          {t('batch.reprint.auditNote')}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
            className="h-12"
          >
            {t('bins.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isConfirming}
            className="h-12"
          >
            {isConfirming ? t('batch.reprint.printing') : t('batch.reprint.reprintLabels')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReprintConfirmDialog;
